import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Download,
  Upload,
  FileArchive,
  AlertCircle,
  Trash2,
} from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import type { MapFile } from '@/types'
import { deleteMapFile, downloadMapFile } from '@/hooks/useMapFiles'
import { apiUpload } from '@/api/client'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import LoadingSpinner from '@/components/common/LoadingSpinner'

interface FileListProps {
  files: MapFile[]
  siteId: string
  robotId: string
  isLoading: boolean
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString()
}

export default function FileList({
  files,
  siteId,
  robotId,
  isLoading,
}: FileListProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const dragCounter = useRef(0)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [uploadSuccess, setUploadSuccess] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState<string[] | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [sortKey, setSortKey] = useState<'filename' | 'size' | 'last_modified'>(
    'filename'
  )
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')

  const sortedFiles = useMemo(() => {
    return [...files].sort((a, b) => {
      let cmp = 0
      switch (sortKey) {
        case 'filename':
          cmp = a.filename.localeCompare(b.filename)
          break
        case 'size':
          cmp = a.size - b.size
          break
        case 'last_modified':
          cmp =
            new Date(a.last_modified).getTime() -
            new Date(b.last_modified).getTime()
          break
      }
      return sortOrder === 'asc' ? cmp : -cmp
    })
  }, [files, sortKey, sortOrder])

  const handleSort = useCallback(
    (key: 'filename' | 'size' | 'last_modified') => {
      if (sortKey === key) {
        setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
      } else {
        setSortKey(key)
        setSortOrder('asc')
      }
    },
    [sortKey]
  )

  const handleDownload = useCallback(
    async (filename: string) => {
      try {
        const url = await downloadMapFile(siteId, robotId, filename)
        window.open(url, '_blank')
      } catch (err) {
        console.error('Download failed:', err)
      }
    },
    [siteId, robotId]
  )

  const handleDelete = useCallback(
    async (filenames: string[]) => {
      setDeleting(true)
      setDeleteError(null)
      try {
        const results = await Promise.allSettled(
          filenames.map((f) => deleteMapFile(siteId, robotId, f))
        )
        const failed = results
          .map((r, i) => ({ r, name: filenames[i] }))
          .filter(({ r }) => r.status === 'rejected')
        queryClient.invalidateQueries({
          queryKey: ['mapFiles', siteId, robotId],
        })
        if (failed.length > 0) {
          setDeleteError(
            t('files.deleteErrorSome', {
              count: failed.length,
              filenames: failed.map((f) => f.name).join(', '),
            })
          )
        }
        setSelected((prev) => {
          const next = new Set(prev)
          const failedNames = new Set(failed.map((f) => f.name))
          for (const name of filenames) {
            if (!failedNames.has(name)) next.delete(name)
          }
          return next
        })
      } catch (err) {
        setDeleteError(
          err instanceof Error ? err.message : t('files.deleteError')
        )
      } finally {
        setDeleting(false)
        setDeleteConfirm(null)
      }
    },
    [siteId, robotId, queryClient, t]
  )

  const toggleSelect = useCallback((filename: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(filename)) next.delete(filename)
      else next.add(filename)
      return next
    })
  }, [])

  const toggleSelectAll = useCallback(() => {
    setSelected((prev) => {
      if (prev.size === sortedFiles.length && sortedFiles.length > 0) {
        return new Set()
      }
      return new Set(sortedFiles.map((f) => f.filename))
    })
  }, [sortedFiles])

  const uploadFile = useCallback(
    async (file: File) => {
      setUploading(true)
      setUploadError(null)
      setUploadSuccess(false)

      try {
        const resp = await apiUpload(
          `/api/map-storage/sites/${siteId}/${robotId}/upload`,
          file
        )
        if (!resp.ok) {
          throw new Error(`Upload failed: ${resp.status}`)
        }
        setUploadSuccess(true)
        queryClient.invalidateQueries({
          queryKey: ['mapFiles', siteId, robotId],
        })
        setTimeout(() => setUploadSuccess(false), 3000)
      } catch (err) {
        setUploadError(
          err instanceof Error ? err.message : t('files.uploadError')
        )
      } finally {
        setUploading(false)
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      }
    },
    [siteId, robotId, queryClient, t]
  )

  const handleUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return
      uploadFile(file)
    },
    [uploadFile]
  )

  const handleDragEnter = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      dragCounter.current++
      if (
        !uploading &&
        e.dataTransfer.items &&
        e.dataTransfer.items.length > 0
      ) {
        setIsDragging(true)
      }
    },
    [uploading]
  )

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current--
    if (dragCounter.current <= 0) {
      dragCounter.current = 0
      setIsDragging(false)
    }
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      dragCounter.current = 0
      setIsDragging(false)

      if (uploading) return

      const files = e.dataTransfer.files
      if (!files || files.length === 0) return

      const file = files[0]
      if (!file.name.toLowerCase().endsWith('.zip')) {
        setUploadError(t('files.invalidFileType'))
        return
      }
      uploadFile(file)
    },
    [uploading, uploadFile, t]
  )

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <LoadingSpinner size="lg" />
        <p className="text-sm text-gray-500">{t('files.loading')}</p>
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          {t('files.title')}
        </h3>
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".zip"
            onChange={handleUpload}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50"
          >
            {uploading ? (
              <LoadingSpinner size="sm" />
            ) : (
              <Upload className="w-4 h-4" />
            )}
            {uploading ? t('files.uploading') : t('files.upload')}
          </button>
        </div>
      </div>

      {uploadSuccess && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
          {t('files.uploadSuccess')}
        </div>
      )}

      {deleteError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {deleteError}
        </div>
      )}

      {uploadError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {uploadError}
        </div>
      )}

      {selected.size > 0 && (
        <div className="mb-4 p-3 bg-primary-50 border border-primary-200 rounded-lg flex items-center justify-between">
          <span className="text-sm text-primary-900">
            {t('files.selectedCount', { count: selected.size })}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelected(new Set())}
              className="px-3 py-1.5 text-sm text-gray-700 hover:bg-white rounded-lg transition-colors"
            >
              {t('files.clearSelection')}
            </button>
            <button
              onClick={() => setDeleteConfirm(Array.from(selected))}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              {t('files.deleteSelected')}
            </button>
          </div>
        </div>
      )}

      <div
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={`relative rounded-lg transition-colors ${
          isDragging
            ? 'ring-2 ring-primary-500 ring-offset-2 bg-primary-50'
            : ''
        }`}
      >
        {isDragging && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-primary-50/80 rounded-lg pointer-events-none">
            <div className="flex flex-col items-center gap-2 text-primary-700">
              <Upload className="w-8 h-8" />
              <p className="font-medium">{t('files.dropToUpload')}</p>
            </div>
          </div>
        )}
        {files.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <FileArchive className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>{t('files.noFiles')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="w-10 py-3 px-4">
                    <input
                      type="checkbox"
                      aria-label={t('files.selectAll')}
                      ref={(el) => {
                        if (el) {
                          el.indeterminate =
                            selected.size > 0 &&
                            selected.size < sortedFiles.length
                        }
                      }}
                      checked={
                        sortedFiles.length > 0 &&
                        selected.size === sortedFiles.length
                      }
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
                    />
                  </th>
                  <th
                    className="text-left py-3 px-4 font-medium text-gray-700 cursor-pointer select-none hover:bg-gray-50"
                    onClick={() => handleSort('filename')}
                  >
                    <div className="flex items-center gap-1">
                      {t('files.filename')}
                      {sortKey === 'filename' ? (
                        sortOrder === 'asc' ? (
                          <ArrowUp className="w-3.5 h-3.5" />
                        ) : (
                          <ArrowDown className="w-3.5 h-3.5" />
                        )
                      ) : (
                        <ArrowUpDown className="w-3.5 h-3.5 text-gray-300" />
                      )}
                    </div>
                  </th>
                  <th
                    className="text-right py-3 px-4 font-medium text-gray-700 cursor-pointer select-none hover:bg-gray-50"
                    onClick={() => handleSort('size')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      {t('files.size')}
                      {sortKey === 'size' ? (
                        sortOrder === 'asc' ? (
                          <ArrowUp className="w-3.5 h-3.5" />
                        ) : (
                          <ArrowDown className="w-3.5 h-3.5" />
                        )
                      ) : (
                        <ArrowUpDown className="w-3.5 h-3.5 text-gray-300" />
                      )}
                    </div>
                  </th>
                  <th
                    className="text-left py-3 px-4 font-medium text-gray-700 cursor-pointer select-none hover:bg-gray-50"
                    onClick={() => handleSort('last_modified')}
                  >
                    <div className="flex items-center gap-1">
                      {t('files.lastModified')}
                      {sortKey === 'last_modified' ? (
                        sortOrder === 'asc' ? (
                          <ArrowUp className="w-3.5 h-3.5" />
                        ) : (
                          <ArrowDown className="w-3.5 h-3.5" />
                        )
                      ) : (
                        <ArrowUpDown className="w-3.5 h-3.5 text-gray-300" />
                      )}
                    </div>
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-700">
                    {t('files.actions')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedFiles.map((file) => (
                  <tr
                    key={file.key}
                    className={`border-b border-gray-100 hover:bg-gray-50 ${
                      selected.has(file.filename) ? 'bg-primary-50/50' : ''
                    }`}
                  >
                    <td className="py-3 px-4">
                      <input
                        type="checkbox"
                        aria-label={t('files.selectRow', {
                          filename: file.filename,
                        })}
                        checked={selected.has(file.filename)}
                        onChange={() => toggleSelect(file.filename)}
                        className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
                      />
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <FileArchive className="w-4 h-4 text-gray-400" />
                        <span className="font-medium text-gray-900">
                          {file.filename}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right text-gray-600">
                      {formatFileSize(file.size)}
                    </td>
                    <td className="py-3 px-4 text-gray-600">
                      {formatDate(file.last_modified)}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleDownload(file.filename)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                        >
                          <Download className="w-4 h-4" />
                          {t('files.download')}
                        </button>
                        <button
                          onClick={() => setDeleteConfirm([file.filename])}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                          {t('files.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={deleteConfirm !== null}
        title={t('files.deleteConfirmTitle')}
        message={
          deleting
            ? t('files.deleting')
            : deleteConfirm && deleteConfirm.length === 1
              ? t('files.deleteConfirmMessage', {
                  filename: deleteConfirm[0],
                })
              : t('files.deleteConfirmMessageMulti', {
                  count: deleteConfirm?.length ?? 0,
                })
        }
        confirmLabel={t('files.delete')}
        cancelLabel={t('files.cancel')}
        isAlert
        confirmDisabled={deleting}
        onConfirm={() => deleteConfirm && handleDelete(deleteConfirm)}
        onCancel={() => setDeleteConfirm(null)}
      />
    </div>
  )
}
